import { useCallback } from 'react';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { startLinkedTasks } from '@/lib/api/generated/api';
import type {
  Error as DomainError,
  StartLinkedTaskResult,
  TaskView,
} from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';

import {
  messageOf,
  sayOutcome,
  type CommandOptions,
  type CommandResult,
} from './commandOutcome';
import { TASK_MUTATION_KEYS } from './taskMutationKeys';

const START_FAILED = 'The waiting tasks could not be started.';

/**
 * Start every created task a parent's open join is waiting on, in one request
 * (spec 140 US2, service spec 125 FR-006). The answer lists each task's
 * outcome; the board and the drawer are re-read from the workspace aggregate
 * once it settles, so the rows that started show as started.
 */
export function useStartLinkedTasks(workspaceId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: TASK_MUTATION_KEYS.startChildren,
    mutationFn: async ({
      taskId,
      keepalive,
    }: {
      taskId: string;
      keepalive?: boolean;
    }): Promise<StartLinkedTaskResult[]> => {
      const response = await startLinkedTasks(
        workspaceId,
        taskId,
        keepalive ? { keepalive } : undefined,
      );
      if (response.status === 200) return response.data.results ?? [];
      if (response.status === 409) {
        throw new Error('This task is not waiting on created tasks any more.');
      }
      // The service's own words when it gave any, so a refusal says why.
      const body = response.data as DomainError | undefined;
      throw new Error(body?.message ?? START_FAILED);
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      }),
  });

  // Said through `sayOutcome`: the "Start N" control disappears once its
  // tasks start, and callbacks handed to `mutate` would be dropped with it
  // (spec 151 FR-001).
  const { mutateAsync } = mutation;
  const reportStartAll = useCallback(
    (
      task: Pick<TaskView, 'id'>,
      options: CommandOptions = {},
    ): Promise<CommandResult> =>
      new Promise<CommandResult>((resolve) => {
        sayOutcome(mutateAsync({ taskId: task.id, ...options }), {
          accepted: (results) => {
            const refused = results.filter((r) => r.outcome === 'refused');
            if (refused.length === 0) {
              resolve({ ok: true });
              return;
            }
            // Each different reason once: one reason for all reads as one cause.
            const reasons = [
              ...new Set(refused.flatMap((r) => (r.reason ? [r.reason] : []))),
            ];
            const message = `${String(refused.length)} of ${String(results.length)} could not start${
              reasons.length > 0 ? `: ${reasons.join('; ')}` : ''
            }`;
            toast.error(message);
            resolve({ ok: false, message });
          },
          failed: (error) => {
            const message = messageOf(error);
            toast.error(message);
            resolve({ ok: false, message });
          },
        });
      }),
    [mutateAsync],
  );

  const startAll = useCallback(
    (task: Pick<TaskView, 'id'>, options?: CommandOptions): void => {
      void reportStartAll(task, options);
    },
    [reportStartAll],
  );

  // `reportStartAll` resolves with how it ended, for the Overview's held
  // actions (spec 154 R2).
  return { startAll, reportStartAll };
}

/** Whether a Start all is in flight for `taskId`, wherever it was pressed. */
export function useStartingLinkedTasks(taskId: string): boolean {
  return (
    useIsMutating({
      mutationKey: TASK_MUTATION_KEYS.startChildren,
      predicate: (mutation) =>
        (mutation.state.variables as { taskId?: string } | undefined)
          ?.taskId === taskId,
    }) > 0
  );
}
